<template>
  <div class="operations-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER ==================== -->
    <div class="op-header-row mb-6">
      <div class="op-header-titles">
        <div class="d-flex align-center gap-2 mb-2 flex-wrap">
          <span class="op-badge-section">
            <v-icon x-small color="#082C69" class="me-1">mdi-bus-clock</v-icon>
            الرحلات
          </span>

          <!-- Dynamic Status Summary Pill -->
          <div :class="['op-summary-pill', `summary-${filters.status}`]">
            <template v-if="filters.status === 'not_started'">
              <v-icon x-small color="#2563EB" class="me-1">mdi-clock-outline</v-icon>
              <span>لم تبدأ: <strong>{{ overview.status_counts.not_started }}</strong> رحلة مجدولة</span>
            </template>
            <template v-else-if="filters.status === 'running'">
              <span class="op-pulse-dot me-1"></span>
              <span>في الطريق: <strong>{{ overview.status_counts.running }}</strong> رحلة جارية</span>
            </template>
            <template v-else-if="filters.status === 'completed'">
              <v-icon x-small color="#059669" class="me-1">mdi-check-circle-outline</v-icon>
              <span>مكتملة: <strong>{{ overview.status_counts.completed }}</strong> رحلة منتهية</span>
            </template>
            <template v-else-if="filters.status === 'cancelled'">
              <v-icon x-small color="#DC2626" class="me-1">mdi-close-circle-outline</v-icon>
              <span>ملغاة: <strong>{{ overview.status_counts.cancelled }}</strong> رحلة ملغاة</span>
            </template>
          </div>
        </div>

        <h1 class="op-main-title">الجدول الزمني للرحلات</h1>
        <p class="op-subtitle">إدارة ومتابعة جميع الرحلات في النظام</p>
      </div>

      <!-- Header Actions -->
      <div class="op-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          class="op-btn-refresh"
          :loading="isLoading"
          :disabled="isLoading"
          @click="fetchTrips"
        >
          <v-icon size="18" color="#082C69">mdi-refresh</v-icon>
          <span class="ms-1 font-weight-bold">تحديث</span>
        </v-btn>
      </div>
    </div>

    <!-- ==================== NO ASSIGNED GROUPS EMPTY STATE ==================== -->
    <div v-if="!isLoading && !overview.has_assignments" class="op-unassigned-card text-center py-12 px-6">
      <div class="op-empty-icon-circle mx-auto mb-4">
        <v-icon size="48" color="#64748B">mdi-folder-alert-outline</v-icon>
      </div>
      <h2 class="op-empty-title mb-2">لا توجد مجموعات تشغيل مسندة إليك حالياً</h2>
      <p class="op-empty-desc mx-auto">
        حسابك التشغيلي نشط، ولكن لم يتم إسناد أي مجموعة تشغيل إليك بعد. يرجى التواصل مع إدارة النظام لتعيين مجموعات التشغيل الخاصة بك لتتمكن من متابعة الجدول الزمني للرحلات وحالاتها.
      </p>
    </div>

    <!-- ==================== CONTENT WHEN ASSIGNED ==================== -->
    <div v-else>
      <!-- ==================== 4 STATUS TABS ==================== -->
      <div class="op-status-tabs-wrapper mb-5">
        <div class="op-status-tabs">
          <!-- 1. لم تبدأ -->
          <button
            type="button"
            :class="['op-tab-btn', filters.status === 'not_started' ? 'tab-active tab-not-started' : '']"
            @click="switchStatusTab('not_started')"
          >
            <v-icon size="18" class="me-1 tab-icon">mdi-clock-outline</v-icon>
            <span class="tab-title">لم تبدأ</span>
            <span class="op-tab-counter counter-not-started">{{ overview.status_counts.not_started }}</span>
          </button>

          <!-- 2. في الطريق -->
          <button
            type="button"
            :class="['op-tab-btn', filters.status === 'running' ? 'tab-active tab-running' : '']"
            @click="switchStatusTab('running')"
          >
            <span v-if="filters.status === 'running'" class="op-pulse-dot me-1"></span>
            <v-icon v-else size="18" class="me-1 tab-icon">mdi-navigation-variant-outline</v-icon>
            <span class="tab-title">في الطريق</span>
            <span class="op-tab-counter counter-running">{{ overview.status_counts.running }}</span>
          </button>

          <!-- 3. مكتملة -->
          <button
            type="button"
            :class="['op-tab-btn', filters.status === 'completed' ? 'tab-active tab-completed' : '']"
            @click="switchStatusTab('completed')"
          >
            <v-icon size="18" class="me-1 tab-icon">mdi-check-circle-outline</v-icon>
            <span class="tab-title">مكتملة</span>
            <span class="op-tab-counter counter-completed">{{ overview.status_counts.completed }}</span>
          </button>

          <!-- 4. ملغاة -->
          <button
            type="button"
            :class="['op-tab-btn', filters.status === 'cancelled' ? 'tab-active tab-cancelled' : '']"
            @click="switchStatusTab('cancelled')"
          >
            <v-icon size="18" class="me-1 tab-icon">mdi-close-circle-outline</v-icon>
            <span class="tab-title">ملغاة</span>
            <span class="op-tab-counter counter-cancelled">{{ overview.status_counts.cancelled }}</span>
          </button>
        </div>
      </div>

      <!-- ==================== FILTER BAR ==================== -->
      <div class="op-filter-bar mb-4">
        <div class="filter-inputs-group">
          <!-- 1. Search Box -->
          <div class="filter-search-box">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              placeholder="البحث برقم الرحلة، المسار، السائق، المركبة..."
              outlined
              dense
              clearable
              hide-details
              class="op-search-input"
              @input="onSearchInput"
              @keydown.enter="onSearchEnter"
              @click:clear="onSearchClear"
            ></v-text-field>
          </div>

          <!-- 2. Date Mode Quick Shortcuts -->
          <div class="filter-date-shortcuts d-flex align-center gap-1">
            <button
              type="button"
              :class="['op-date-chip', filters.date_mode === 'all' ? 'chip-active' : '']"
              @click="setDateMode('all')"
            >
              الكل
            </button>
            <button
              type="button"
              :class="['op-date-chip', filters.date_mode === 'today' ? 'chip-active' : '']"
              @click="setDateMode('today')"
            >
              اليوم
            </button>
            <button
              type="button"
              :class="['op-date-chip', filters.date_mode === 'tomorrow' ? 'chip-active' : '']"
              @click="setDateMode('tomorrow')"
            >
              غداً
            </button>
            <button
              type="button"
              :class="['op-date-chip', filters.date_mode === 'week' ? 'chip-active' : '']"
              @click="setDateMode('week')"
            >
              هذا الأسبوع
            </button>
            <button
              type="button"
              :class="['op-date-chip', filters.date_mode === 'custom' ? 'chip-active' : '']"
              @click="toggleCustomDate"
            >
              <v-icon x-small class="me-1">mdi-calendar-range</v-icon>
              مخصص
            </button>
          </div>

          <!-- Custom Date Range Inputs (Shown when custom mode active) -->
          <div v-if="filters.date_mode === 'custom'" class="filter-custom-date d-flex align-center gap-2">
            <v-text-field
              v-model="filters.date_from"
              type="date"
              label="من تاريخ"
              outlined
              dense
              hide-details
              class="op-filter-date"
              @change="onFilterChange"
            ></v-text-field>
            <v-text-field
              v-model="filters.date_to"
              type="date"
              label="إلى تاريخ"
              outlined
              dense
              hide-details
              class="op-filter-date"
              @change="onFilterChange"
            ></v-text-field>
          </div>

          <!-- 3. Route Filter -->
          <div class="filter-select-box">
            <v-select
              v-model="filters.route_id"
              :items="routeFilterOptions"
              label="المسار"
              outlined
              dense
              hide-details
              class="op-filter-select"
              @change="onFilterChange"
            ></v-select>
          </div>

          <!-- 4. Driver Filter -->
          <div class="filter-select-box">
            <v-select
              v-model="filters.driver_id"
              :items="driverFilterOptions"
              label="السائق"
              outlined
              dense
              hide-details
              class="op-filter-select"
              @change="onFilterChange"
            ></v-select>
          </div>

          <!-- 5. Group Filter (if multiple groups) -->
          <div v-if="overview.filter_options && overview.filter_options.groups && overview.filter_options.groups.length > 1" class="filter-select-box">
            <v-select
              v-model="filters.group_id"
              :items="groupFilterOptions"
              label="المجموعة"
              outlined
              dense
              hide-details
              class="op-filter-select"
              @change="onFilterChange"
            ></v-select>
          </div>
        </div>

        <!-- Filter Actions -->
        <div class="filter-actions-group">
          <button type="button" class="op-filter-btn" @click="resetFilters">
            <v-icon size="16" color="#63738A" class="me-1">mdi-restore</v-icon>
            <span>إعادة تعيين</span>
          </button>
        </div>
      </div>

      <!-- ==================== RESULTS COUNT BAR ==================== -->
      <div class="op-results-meta d-flex align-center justify-space-between mb-3 px-1">
        <div class="d-flex align-center gap-2">
          <span class="caption text-muted font-weight-bold">
            تم العثور على <strong class="text-navy font-weight-bold">{{ pagination.total }}</strong> {{ pagination.total === 1 ? 'رحلة' : 'رحلات' }}
          </span>
          <span v-if="filters.search || filters.date_mode !== 'all' || filters.route_id !== 'all' || filters.driver_id !== 'all' || filters.group_id !== 'all'" class="caption text-emerald">
            (مفلترة)
          </span>
        </div>

        <!-- Rows per page selector -->
        <div class="d-flex align-center gap-2">
          <span class="caption text-muted">عدد السجلات:</span>
          <select v-model.number="pagination.per_page" class="op-per-page-select" @change="onPerPageChange">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </div>
      </div>

      <!-- ==================== EMPTY RESULTS STATE ==================== -->
      <div v-if="!isLoading && (!trips || trips.length === 0)" class="op-empty-table-card text-center py-10 px-4 mb-6">
        <v-icon size="44" color="#94A3B8" class="mb-2">mdi-bus-stop-uncovered</v-icon>
        <h3 class="font-weight-bold text-navy mb-1">لا توجد رحلات</h3>
        <p class="caption text-muted mb-4">
          لم يتم العثور على رحلات في حالة "{{ getStatusLabel(filters.status) }}" مطابقة لمعايير البحث والتصفية المحددة.
        </p>
        <v-btn small depressed color="#082C69" dark @click="resetFilters">
          إلغاء التصفية وإظهار الكل
        </v-btn>
      </div>

      <!-- ==================== MAIN STATE-SPECIFIC TABLES ==================== -->
      <div v-else class="op-main-table-card mb-4">
        <div class="table-responsive">
          <!-- ==================== TAB 1: لم تبدأ ==================== -->
          <table v-if="filters.status === 'not_started'" class="op-data-table">
            <thead>
              <tr>
                <th style="width: 44px;" class="text-center">#</th>
                <th style="width: 140px;">كود الرحلة</th>
                <th style="width: 170px;">اليوم والتاريخ</th>
                <th>المسار</th>
                <th style="width: 160px;">وقت الانطلاق</th>
                <th style="width: 150px;">وقت الوصول المتوقع</th>
                <th class="text-center" style="width: 110px;">الحجوزات</th>
                <th style="width: 180px;">السائق</th>
                <th style="width: 150px;">وسيلة النقل</th>
                <th class="text-center" style="width: 120px;">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(trip, idx) in trips" :key="trip.id" class="op-table-row">
                <!-- Index -->
                <td class="text-center text-muted font-weight-medium">
                  {{ (pagination.current_page - 1) * pagination.per_page + idx + 1 }}
                </td>

                <!-- Codes -->
                <td>
                  <div class="d-flex flex-column">
                    <span class="op-trip-code-pill font-monospace font-weight-bold">{{ trip.planned_trip_code }}</span>
                    <span class="op-parent-ref caption text-muted mt-0.5">أساسي: {{ trip.parent_trip_code }}</span>
                  </div>
                </td>

                <!-- Day & Date -->
                <td>
                  <div class="d-flex flex-column">
                    <span class="font-weight-bold text-navy">{{ trip.day_name }}</span>
                    <span class="caption text-muted mt-0.5">{{ trip.planned_date_formatted }}</span>
                  </div>
                </td>

                <!-- Route -->
                <td>
                  <div class="d-flex flex-column">
                    <span class="font-weight-bold text-navy">{{ trip.route_name }}</span>
                    <span class="op-route-code-pill font-monospace mt-0.5">{{ trip.route_code }}</span>
                  </div>
                </td>

                <!-- Start Time + Relative Badge -->
                <td>
                  <div class="d-flex flex-column">
                    <span class="font-weight-bold text-navy">{{ trip.first_stop_time_formatted }}</span>
                    <div v-if="trip.relative_start_badge" class="mt-1">
                      <span :class="['op-relative-badge', `rel-${trip.relative_start_badge.color}`]">
                        {{ trip.relative_start_badge.label }}
                      </span>
                    </div>
                  </div>
                </td>

                <!-- Expected Arrival -->
                <td>
                  <div class="d-flex flex-column">
                    <span class="font-weight-bold text-navy">{{ trip.last_stop_time_formatted }}</span>
                    <span v-if="trip.expected_duration" class="caption text-muted mt-0.5">({{ trip.expected_duration }})</span>
                  </div>
                </td>

                <!-- Bookings -->
                <td class="text-center">
                  <span class="op-bookings-pill">
                    <v-icon x-small color="#082C69" class="me-1">mdi-account-group-outline</v-icon>
                    <strong>{{ trip.reserved_seats || trip.bookings_count || 0 }}</strong> مقعد
                  </span>
                </td>

                <!-- Driver -->
                <td>
                  <div v-if="trip.driver" class="d-flex align-center gap-2">
                    <div class="op-driver-avatar">
                      <v-icon size="14" color="#082C69">mdi-account-tie</v-icon>
                    </div>
                    <div class="d-flex flex-column">
                      <span class="font-weight-bold text-navy body-2">{{ trip.driver.name }}</span>
                      <span v-if="trip.driver.phone" class="caption text-muted font-monospace">{{ trip.driver.phone }}</span>
                    </div>
                  </div>
                  <div v-else>
                    <span class="op-badge-unassigned">قيد التعيين</span>
                  </div>
                </td>

                <!-- Vehicle -->
                <td>
                  <div v-if="trip.vehicle" class="d-flex flex-column">
                    <span class="font-weight-bold text-navy body-2">{{ trip.vehicle.name }}</span>
                    <span class="caption text-muted font-monospace">{{ trip.vehicle.plate || trip.vehicle.model }}</span>
                  </div>
                  <span v-else class="caption text-muted">غير محدد</span>
                </td>

                <!-- Actions -->
                <td class="text-center" @click.stop>
                  <div class="d-flex align-center justify-center gap-1">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          small
                          depressed
                          class="op-btn-view"
                          v-bind="attrs"
                          v-on="on"
                          @click="openTripDetails(trip)"
                        >
                          <v-icon size="14" class="me-1">mdi-eye-outline</v-icon>
                          <span>عرض</span>
                        </v-btn>
                      </template>
                      <span>عرض تفاصيل ومحطات الرحلة</span>
                    </v-tooltip>

                    <!-- Three-dot menu -->
                    <v-menu offset-y left nudge-bottom="6">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          class="op-btn-menu-icon"
                        >
                          <v-icon size="16">mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense class="py-1 op-action-menu-list">
                        <v-list-item v-if="trip.can_pause" link @click="promptPauseTrip(trip)">
                          <v-list-item-icon class="me-2 my-1">
                            <v-icon size="16" color="#D97706">mdi-pause-circle-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="font-weight-medium">إيقاف مؤقت للرحلة</v-list-item-title>
                        </v-list-item>

                        <v-list-item v-if="trip.can_resume" link @click="promptResumeTrip(trip)">
                          <v-list-item-icon class="me-2 my-1">
                            <v-icon size="16" color="#059669">mdi-play-circle-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="font-weight-medium text-emerald">استئناف الرحلة</v-list-item-title>
                        </v-list-item>

                        <v-list-item link @click="openTripDetails(trip)">
                          <v-list-item-icon class="me-2 my-1">
                            <v-icon size="16" color="#082C69">mdi-map-marker-path</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="font-weight-medium">عرض المحطات على الخريطة</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- ==================== TAB 2: في الطريق ==================== -->
          <table v-else-if="filters.status === 'running'" class="op-data-table">
            <thead>
              <tr>
                <th style="width: 44px;" class="text-center">#</th>
                <th style="width: 140px;">كود الرحلة</th>
                <th style="width: 150px;">التاريخ</th>
                <th>المسار</th>
                <th style="width: 150px;">وقت الانطلاق الفعلي</th>
                <th style="width: 150px;">الوصول المتوقع</th>
                <th class="text-center" style="width: 100px;">الحجوزات</th>
                <th style="width: 170px;">السائق</th>
                <th style="width: 150px;">وسيلة النقل</th>
                <th class="text-center" style="width: 120px;">الحالة</th>
                <th class="text-center" style="width: 90px;">عرض</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(trip, idx) in trips" :key="trip.id" class="op-table-row">
                <td class="text-center text-muted font-weight-medium">
                  {{ (pagination.current_page - 1) * pagination.per_page + idx + 1 }}
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="op-trip-code-pill font-monospace font-weight-bold">{{ trip.planned_trip_code }}</span>
                    <span class="op-parent-ref caption text-muted mt-0.5">أساسي: {{ trip.parent_trip_code }}</span>
                  </div>
                </td>
                <td>
                  <span class="font-weight-bold text-navy">{{ trip.planned_date_formatted }}</span>
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="font-weight-bold text-navy">{{ trip.route_name }}</span>
                    <span class="op-route-code-pill font-monospace mt-0.5">{{ trip.route_code }}</span>
                  </div>
                </td>
                <td>
                  <span class="font-weight-bold text-emerald font-monospace">{{ trip.actual_start_time || trip.first_stop_time_formatted }}</span>
                </td>
                <td>
                  <span class="font-weight-bold text-navy font-monospace">{{ trip.last_stop_time_formatted }}</span>
                </td>
                <td class="text-center">
                  <span class="op-bookings-pill">
                    <strong>{{ trip.reserved_seats || trip.bookings_count || 0 }}</strong> مقعد
                  </span>
                </td>
                <td>
                  <div v-if="trip.driver" class="d-flex align-center gap-2">
                    <div class="op-driver-avatar">
                      <v-icon size="14" color="#082C69">mdi-account-tie</v-icon>
                    </div>
                    <span class="font-weight-bold text-navy body-2">{{ trip.driver.name }}</span>
                  </div>
                  <span v-else class="caption text-muted">—</span>
                </td>
                <td>
                  <div v-if="trip.vehicle" class="d-flex flex-column">
                    <span class="font-weight-bold text-navy body-2">{{ trip.vehicle.name }}</span>
                    <span class="caption text-muted font-monospace">{{ trip.vehicle.plate || trip.vehicle.model }}</span>
                  </div>
                  <span v-else class="caption text-muted">—</span>
                </td>
                <td class="text-center">
                  <span class="op-live-running-badge d-inline-flex align-center">
                    <span class="op-pulse-dot me-1"></span>
                    <span>في الطريق</span>
                  </span>
                </td>
                <td class="text-center">
                  <v-btn small depressed class="op-btn-view" @click="openTripDetails(trip)">
                    <v-icon size="14" class="me-1">mdi-eye-outline</v-icon>
                    <span>عرض</span>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- ==================== TAB 3: مكتملة ==================== -->
          <table v-else-if="filters.status === 'completed'" class="op-data-table">
            <thead>
              <tr>
                <th style="width: 44px;" class="text-center">#</th>
                <th style="width: 140px;">كود الرحلة</th>
                <th style="width: 150px;">التاريخ</th>
                <th>المسار</th>
                <th style="width: 120px;">وقت الانطلاق</th>
                <th style="width: 120px;">الوصول المجدول</th>
                <th style="width: 180px;">الوصول الفعلي</th>
                <th class="text-center" style="width: 90px;">الحجوزات</th>
                <th style="width: 160px;">السائق</th>
                <th style="width: 150px;">وسيلة النقل</th>
                <th class="text-center" style="width: 90px;">عرض</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(trip, idx) in trips" :key="trip.id" class="op-table-row">
                <td class="text-center text-muted font-weight-medium">
                  {{ (pagination.current_page - 1) * pagination.per_page + idx + 1 }}
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="op-trip-code-pill font-monospace font-weight-bold">{{ trip.planned_trip_code }}</span>
                    <span class="op-parent-ref caption text-muted mt-0.5">أساسي: {{ trip.parent_trip_code }}</span>
                  </div>
                </td>
                <td>
                  <span class="font-weight-bold text-navy">{{ trip.planned_date_formatted }}</span>
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="font-weight-bold text-navy">{{ trip.route_name }}</span>
                    <span class="op-route-code-pill font-monospace mt-0.5">{{ trip.route_code }}</span>
                  </div>
                </td>
                <td>
                  <span class="font-weight-bold text-navy font-monospace">{{ trip.actual_start_time || trip.first_stop_time_formatted }}</span>
                </td>
                <td>
                  <span class="font-weight-bold text-navy font-monospace">{{ trip.last_stop_time_formatted }}</span>
                </td>
                <!-- Actual end time + completion timing badge -->
                <td>
                  <div class="d-flex flex-column">
                    <span class="font-weight-bold text-navy font-monospace">{{ trip.actual_end_time || trip.last_stop_time_formatted }}</span>
                    <div v-if="trip.completion_timing_badge" class="mt-1">
                      <span :class="['op-timing-badge', `timing-${trip.completion_timing_badge.type}`]">
                        {{ trip.completion_timing_badge.label }}
                      </span>
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <span class="op-bookings-pill">
                    <strong>{{ trip.reserved_seats || trip.bookings_count || 0 }}</strong> مقعد
                  </span>
                </td>
                <td>
                  <div v-if="trip.driver" class="d-flex align-center gap-2">
                    <div class="op-driver-avatar">
                      <v-icon size="14" color="#082C69">mdi-account-tie</v-icon>
                    </div>
                    <span class="font-weight-bold text-navy body-2">{{ trip.driver.name }}</span>
                  </div>
                  <span v-else class="caption text-muted">—</span>
                </td>
                <td>
                  <div v-if="trip.vehicle" class="d-flex flex-column">
                    <span class="font-weight-bold text-navy body-2">{{ trip.vehicle.name }}</span>
                    <span class="caption text-muted font-monospace">{{ trip.vehicle.plate || trip.vehicle.model }}</span>
                  </div>
                  <span v-else class="caption text-muted">—</span>
                </td>
                <td class="text-center">
                  <v-btn small depressed class="op-btn-view" @click="openTripDetails(trip)">
                    <v-icon size="14" class="me-1">mdi-eye-outline</v-icon>
                    <span>عرض</span>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- ==================== TAB 4: ملغاة ==================== -->
          <table v-else-if="filters.status === 'cancelled'" class="op-data-table">
            <thead>
              <tr>
                <th style="width: 44px;" class="text-center">#</th>
                <th style="width: 140px;">كود الرحلة</th>
                <th style="width: 150px;">التاريخ</th>
                <th>المسار</th>
                <th style="width: 130px;">الموعد المجدول</th>
                <th>سبب الإلغاء</th>
                <th class="text-center" style="width: 90px;">الحجوزات</th>
                <th style="width: 160px;">السائق</th>
                <th style="width: 150px;">وسيلة النقل</th>
                <th class="text-center" style="width: 90px;">عرض</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(trip, idx) in trips" :key="trip.id" class="op-table-row">
                <td class="text-center text-muted font-weight-medium">
                  {{ (pagination.current_page - 1) * pagination.per_page + idx + 1 }}
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="op-trip-code-pill font-monospace font-weight-bold text-danger">{{ trip.planned_trip_code }}</span>
                    <span class="op-parent-ref caption text-muted mt-0.5">أساسي: {{ trip.parent_trip_code }}</span>
                  </div>
                </td>
                <td>
                  <span class="font-weight-bold text-navy">{{ trip.planned_date_formatted }}</span>
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="font-weight-bold text-navy">{{ trip.route_name }}</span>
                    <span class="op-route-code-pill font-monospace mt-0.5">{{ trip.route_code }}</span>
                  </div>
                </td>
                <td>
                  <span class="font-weight-bold text-navy font-monospace">{{ trip.first_stop_time_formatted }}</span>
                </td>
                <!-- Cancellation Reason Badge -->
                <td>
                  <div class="op-cancellation-badge d-inline-flex align-start">
                    <v-icon size="15" color="#DC2626" class="me-1 mt-0.5 flex-shrink-0">mdi-alert-circle-outline</v-icon>
                    <div class="d-flex flex-column">
                      <span class="reason-text font-weight-bold">{{ trip.cancellation_reason || 'سبب غير مسجل' }}</span>
                      <span v-if="trip.cancellation_time" class="caption text-muted mt-0.5">ألغيت في: {{ trip.cancellation_time }}</span>
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <span class="op-bookings-pill">
                    <strong>{{ trip.reserved_seats || trip.bookings_count || 0 }}</strong> مقعد
                  </span>
                </td>
                <td>
                  <div v-if="trip.driver" class="d-flex align-center gap-2">
                    <div class="op-driver-avatar">
                      <v-icon size="14" color="#082C69">mdi-account-tie</v-icon>
                    </div>
                    <span class="font-weight-bold text-navy body-2">{{ trip.driver.name }}</span>
                  </div>
                  <span v-else class="caption text-muted">—</span>
                </td>
                <td>
                  <div v-if="trip.vehicle" class="d-flex flex-column">
                    <span class="font-weight-bold text-navy body-2">{{ trip.vehicle.name }}</span>
                    <span class="caption text-muted font-monospace">{{ trip.vehicle.plate || trip.vehicle.model }}</span>
                  </div>
                  <span v-else class="caption text-muted">—</span>
                </td>
                <td class="text-center">
                  <v-btn small depressed class="op-btn-view" @click="openTripDetails(trip)">
                    <v-icon size="14" class="me-1">mdi-eye-outline</v-icon>
                    <span>عرض</span>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ==================== PAGINATION CONTROLS ==================== -->
        <div class="op-table-footer d-flex align-center justify-space-between flex-wrap gap-3 py-3 px-4">
          <div class="caption text-muted">
            عرض <strong>{{ pagination.from }}</strong> إلى <strong>{{ pagination.to }}</strong> من إجمالي <strong>{{ pagination.total }}</strong> رحلة
          </div>

          <div v-if="pagination.last_page > 1" class="op-pagination-buttons d-flex align-center gap-1">
            <button
              type="button"
              class="op-page-btn"
              :disabled="pagination.current_page <= 1"
              @click="goToPage(pagination.current_page - 1)"
            >
              <v-icon small>mdi-chevron-right</v-icon>
              <span>السابق</span>
            </button>

            <template v-for="page in visiblePages">
              <button
                v-if="page !== '...'"
                :key="`page-${page}`"
                type="button"
                :class="['op-page-number', pagination.current_page === page ? 'page-active' : '']"
                @click="goToPage(page)"
              >
                {{ page }}
              </button>
              <span v-else :key="`ellipsis-${Math.random()}`" class="op-page-ellipsis">...</span>
            </template>

            <button
              type="button"
              class="op-page-btn"
              :disabled="pagination.current_page >= pagination.last_page"
              @click="goToPage(pagination.current_page + 1)"
            >
              <span>التالي</span>
              <v-icon small>mdi-chevron-left</v-icon>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== VIEW-ONLY TRIP DETAILS MODAL ==================== -->
    <v-dialog v-model="tripDetailsDialog" max-width="850px" scrollable>
      <v-card v-if="selectedTripDetails" class="op-dialog-card" dir="rtl">
        <!-- Dialog Header -->
        <v-card-title class="op-dialog-header d-flex align-center justify-space-between py-4 px-6">
          <div class="d-flex align-center gap-2 flex-wrap">
            <div class="op-icon-header-box me-2">
              <v-icon size="20" color="#082C69">mdi-bus</v-icon>
            </div>
            <div>
              <div class="d-flex align-center gap-2">
                <span class="op-trip-code-pill font-monospace font-weight-bold">{{ selectedTripDetails.trip.planned_trip_code }}</span>
                <span class="op-route-code-pill font-monospace">{{ selectedTripDetails.route.route_code }}</span>
                <span :class="['op-trip-status-pill', `status-${selectedTripDetails.trip.status}`]">
                  {{ selectedTripDetails.trip.status_label }}
                </span>
                <span v-if="selectedTripDetails.trip.is_paused" class="op-trip-status-pill status-unassigned">
                  متوقفة مؤقتاً
                </span>
              </div>
              <h3 class="op-dialog-title mb-0 mt-1">{{ selectedTripDetails.route.name }}</h3>
            </div>
          </div>
          <v-btn icon small @click="tripDetailsDialog = false">
            <v-icon color="#64748B">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <!-- Cancellation Banner if Cancelled -->
          <div v-if="selectedTripDetails.trip.is_cancelled" class="op-modal-cancel-banner mb-4 pa-3 rounded-lg d-flex align-center">
            <v-icon color="#DC2626" class="me-2">mdi-alert-octagon</v-icon>
            <div>
              <span class="font-weight-bold text-danger d-block">هذه الرحلة ملغاة</span>
              <span class="caption text-navy">{{ selectedTripDetails.trip.cancellation_reason || 'سبب غير مسجل' }}</span>
            </div>
          </div>

          <!-- 4 Stat Cards Grid -->
          <div class="op-trip-modal-grid mb-5">
            <div class="op-modal-stat-card">
              <span class="stat-label">التاريخ</span>
              <span class="stat-value text-navy">{{ selectedTripDetails.trip.planned_date_formatted || '—' }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">وقت الانطلاق</span>
              <span class="stat-value text-navy">{{ selectedTripDetails.trip.first_stop_time_formatted }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">وقت الوصول المتوقع</span>
              <span class="stat-value text-navy">{{ selectedTripDetails.trip.last_stop_time_formatted }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">السائق المسند</span>
              <span class="stat-value font-weight-bold" :class="selectedTripDetails.trip.driver ? 'text-emerald' : 'text-amber'">
                {{ selectedTripDetails.trip.driver ? selectedTripDetails.trip.driver.name : 'قيد التعيين' }}
              </span>
            </div>
          </div>

          <!-- Vehicle Info if available -->
          <div v-if="selectedTripDetails.trip.vehicle" class="op-modal-vehicle-card mb-4 pa-3 rounded-lg d-flex align-center justify-space-between">
            <div class="d-flex align-center gap-2">
              <v-icon color="#082C69">mdi-bus</v-icon>
              <div>
                <span class="caption text-muted d-block">وسيلة النقل المسندة</span>
                <span class="font-weight-bold text-navy">{{ selectedTripDetails.trip.vehicle.name }}</span>
              </div>
            </div>
            <span class="font-monospace caption font-weight-bold text-navy bg-slate-100 px-2 py-1 rounded">
              لوحة: {{ selectedTripDetails.trip.vehicle.plate || selectedTripDetails.trip.vehicle.model }}
            </span>
          </div>

          <!-- Stops Timetable -->
          <h4 class="font-weight-bold text-navy mb-3 d-flex align-center">
            <v-icon small color="#082C69" class="me-1">mdi-map-marker-path</v-icon>
            محطات المسار ومواعيد الوصول المقدرة ({{ selectedTripDetails.stops.length }} محطة)
          </h4>

          <div class="table-responsive border rounded-lg mb-5">
            <table class="op-trips-table">
              <thead>
                <tr>
                  <th style="width: 40px;" class="text-center">#</th>
                  <th>اسم المحطة</th>
                  <th>العنوان</th>
                  <th>وقت الوصول (ETA)</th>
                  <th class="text-center">زمن الطريق (دقيقة)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stop in selectedTripDetails.stops" :key="stop.order">
                  <td class="text-center text-muted font-weight-bold">{{ stop.order }}</td>
                  <td class="font-weight-bold text-navy">{{ stop.name }}</td>
                  <td class="caption text-muted">{{ stop.address || '—' }}</td>
                  <td class="caption font-weight-bold text-emerald">{{ stop.eta_formatted || '—' }}</td>
                  <td class="text-center caption">{{ stop.road_minutes_from_previous || '0' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Google Map View -->
          <div v-if="selectedTripDetails.map && selectedTripDetails.map.markers && selectedTripDetails.map.markers.length > 0" class="mt-4">
            <h4 class="font-weight-bold text-navy mb-2 d-flex align-center">
              <v-icon small color="#082C69" class="me-1">mdi-map-outline</v-icon>
              خريطة المسار والمحطات
            </h4>
            <div class="op-modal-map-container">
              <GoogleMapLoader
                :apiKey="googleMapsApiKey"
                :mapConfig="mapConfig"
                :center="mapCenter"
                :zoom="12"
                :markers="mapMarkers"
                :polylines="mapPolylines"
                class="op-map-element"
              />
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 bg-slate-50 justify-end">
          <v-btn depressed class="op-btn-secondary" @click="tripDetailsDialog = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== CONFIRMATION DIALOG (PAUSE / RESUME) ==================== -->
    <v-dialog v-model="actionConfirmDialog.visible" max-width="480px">
      <v-card class="op-confirm-card" dir="rtl">
        <v-card-title class="py-4 px-6 d-flex align-center">
          <v-icon size="24" :color="actionConfirmDialog.type === 'pause' ? '#D97706' : '#059669'" class="me-2">
            {{ actionConfirmDialog.type === 'pause' ? 'mdi-pause-circle-outline' : 'mdi-play-circle-outline' }}
          </v-icon>
          <span class="font-weight-bold text-navy">{{ actionConfirmDialog.title }}</span>
        </v-card-title>
        <v-card-text class="px-6 py-2">
          <p class="body-2 text-slate-700 mb-2">{{ actionConfirmDialog.message }}</p>
          <div v-if="actionConfirmDialog.trip" class="bg-slate-50 pa-3 rounded-lg border font-weight-bold text-navy caption">
            رحلة: {{ actionConfirmDialog.trip.planned_trip_code }} — {{ actionConfirmDialog.trip.route_name }}
          </div>
        </v-card-text>
        <v-card-actions class="px-6 py-4 justify-end gap-2">
          <v-btn text :disabled="actionConfirmDialog.loading" @click="actionConfirmDialog.visible = false">
            إلغاء
          </v-btn>
          <v-btn
            depressed
            :loading="actionConfirmDialog.loading"
            :color="actionConfirmDialog.type === 'pause' ? '#D97706' : '#059669'"
            class="white--text font-weight-bold"
            @click="executeActionConfirm"
          >
            تأكيد
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { Keys } from "@/config.js";
import GoogleMapLoader from "@/components/GoogleMapLoader.vue";

export default {
  name: "OperationsTripsTimeline",
  components: {
    GoogleMapLoader,
  },
  data() {
    return {
      isLoading: false,
      overview: {
        has_assignments: false,
        status_counts: {
          not_started: 0,
          running: 0,
          completed: 0,
          cancelled: 0,
          total: 0,
        },
        selected_status: "not_started",
        filter_options: {
          groups: [],
          routes: [],
          drivers: [],
          vehicles: [],
        },
      },
      trips: [],
      pagination: {
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
      },
      filters: {
        status: "not_started",
        search: "",
        date_mode: "all",
        date_from: "",
        date_to: "",
        route_id: "all",
        driver_id: "all",
        group_id: "all",
      },
      searchDebounceTimer: null,

      // Trip Details Modal
      tripDetailsDialog: false,
      selectedTripDetails: null,
      googleMapsApiKey: Keys.GOOGLE_MAPS_API_KEY || "",
      mapConfig: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
      },

      // Action Dialog (Pause/Resume)
      actionConfirmDialog: {
        visible: false,
        loading: false,
        type: "pause", // pause | resume
        title: "",
        message: "",
        trip: null,
      },
    };
  },
  computed: {
    routeFilterOptions() {
      const opts = [{ value: "all", text: "كل المسارات" }];
      if (this.overview.filter_options && this.overview.filter_options.routes) {
        this.overview.filter_options.routes.forEach((r) => {
          opts.push({ value: r.id, text: r.label || r.name });
        });
      }
      return opts;
    },
    driverFilterOptions() {
      const opts = [
        { value: "all", text: "كل السائقين" },
        { value: "unassigned", text: "قيد التعيين (بدون سائق)" },
      ];
      if (this.overview.filter_options && this.overview.filter_options.drivers) {
        this.overview.filter_options.drivers.forEach((d) => {
          opts.push({ value: d.id, text: d.label || d.name });
        });
      }
      return opts;
    },
    groupFilterOptions() {
      const opts = [{ value: "all", text: "كل المجموعات المسندة" }];
      if (this.overview.filter_options && this.overview.filter_options.groups) {
        this.overview.filter_options.groups.forEach((g) => {
          opts.push({ value: g.id, text: g.label || g.name });
        });
      }
      return opts;
    },
    visiblePages() {
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];
      let l;

      for (let i = 1; i <= last; i++) {
        if (i === 1 || i === last || (i >= current - delta && i <= current + delta)) {
          range.push(i);
        }
      }

      for (const i of range) {
        if (l) {
          if (i - l === 2) {
            rangeWithDots.push(l + 1);
          } else if (i - l !== 1) {
            rangeWithDots.push("...");
          }
        }
        rangeWithDots.push(i);
        l = i;
      }

      return rangeWithDots;
    },
    mapCenter() {
      if (
        this.selectedTripDetails &&
        this.selectedTripDetails.map &&
        this.selectedTripDetails.map.markers &&
        this.selectedTripDetails.map.markers.length > 0
      ) {
        return this.selectedTripDetails.map.markers[0].position;
      }
      return { lat: 24.7136, lng: 46.6753 }; // Riyadh default center
    },
    mapMarkers() {
      if (
        this.selectedTripDetails &&
        this.selectedTripDetails.map &&
        this.selectedTripDetails.map.markers
      ) {
        return this.selectedTripDetails.map.markers;
      }
      return [];
    },
    mapPolylines() {
      if (
        this.selectedTripDetails &&
        this.selectedTripDetails.map &&
        this.selectedTripDetails.map.polyline &&
        this.selectedTripDetails.map.polyline.length > 0
      ) {
        return [
          {
            data: this.selectedTripDetails.map.polyline,
            strokeColor: "#082C69",
            strokeWeight: 5,
            strokeOpacity: 0.9,
          },
        ];
      }
      return [];
    },
  },
  created() {
    // If URL query contains status, respect it
    if (this.$route && this.$route.query && this.$route.query.status) {
      const qStatus = this.$route.query.status;
      if (["not_started", "running", "completed", "cancelled"].includes(qStatus)) {
        this.filters.status = qStatus;
      }
    }
    this.fetchTrips();
  },
  methods: {
    fetchTrips() {
      this.isLoading = true;
      const params = {
        status: this.filters.status,
        page: this.pagination.current_page,
        per_page: this.pagination.per_page,
      };

      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.date_mode) params.date_mode = this.filters.date_mode;
      if (this.filters.date_mode === "custom") {
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
      }
      if (this.filters.route_id && this.filters.route_id !== "all") params.route_id = this.filters.route_id;
      if (this.filters.driver_id && this.filters.driver_id !== "all") params.driver_id = this.filters.driver_id;
      if (this.filters.group_id && this.filters.group_id !== "all") params.group_id = this.filters.group_id;

      axios
        .get("/operations/trips", { params })
        .then((response) => {
          if (response.data && response.data.success) {
            const result = response.data.data;
            this.overview.has_assignments = result.has_assignments;
            this.overview.status_counts = result.status_counts || this.overview.status_counts;
            this.overview.filter_options = result.filter_options || this.overview.filter_options;
            this.trips = result.data || [];
            this.pagination = result.pagination || this.pagination;
          }
        })
        .catch((error) => {
          console.error("Error fetching operations trips:", error);
          this.notifyUser("error", "خطأ", "تعذر تحميل الجدول الزمني للرحلات.");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    switchStatusTab(statusKey) {
      if (this.filters.status === statusKey) return;
      this.filters.status = statusKey;
      this.pagination.current_page = 1;
      this.fetchTrips();
    },
    setDateMode(mode) {
      this.filters.date_mode = mode;
      if (mode !== "custom") {
        this.filters.date_from = "";
        this.filters.date_to = "";
      }
      this.pagination.current_page = 1;
      this.fetchTrips();
    },
    toggleCustomDate() {
      if (this.filters.date_mode === "custom") {
        this.setDateMode("all");
      } else {
        this.filters.date_mode = "custom";
      }
    },
    onFilterChange() {
      this.pagination.current_page = 1;
      this.fetchTrips();
    },
    onPerPageChange() {
      this.pagination.current_page = 1;
      this.fetchTrips();
    },
    goToPage(page) {
      if (page < 1 || page > this.pagination.last_page || page === this.pagination.current_page) return;
      this.pagination.current_page = page;
      this.fetchTrips();
    },
    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.current_page = 1;
        this.fetchTrips();
      }, 350);
    },
    onSearchEnter() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.pagination.current_page = 1;
      this.fetchTrips();
    },
    onSearchClear() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.filters.search = "";
      this.pagination.current_page = 1;
      this.$nextTick(() => {
        this.fetchTrips();
      });
    },
    resetFilters() {
      this.filters.search = "";
      this.filters.date_mode = "all";
      this.filters.date_from = "";
      this.filters.date_to = "";
      this.filters.route_id = "all";
      this.filters.driver_id = "all";
      this.filters.group_id = "all";
      this.pagination.current_page = 1;
      this.fetchTrips();
    },
    openTripDetails(trip) {
      this.isLoading = true;
      axios
        .get(`/operations/trips/${trip.id}`)
        .then((res) => {
          if (res.data && res.data.success) {
            this.selectedTripDetails = res.data.data;
            this.tripDetailsDialog = true;
          }
        })
        .catch((err) => {
          console.error("Error loading trip details:", err);
          const msg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "تعذر تحميل تفاصيل الرحلة.";
          this.notifyUser("error", "خطأ", msg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    promptPauseTrip(trip) {
      this.actionConfirmDialog = {
        visible: true,
        loading: false,
        type: "pause",
        title: "إيقاف مؤقت للرحلة",
        message: "هل أنت متأكد من رغبتك في إيقاف هذه الرحلة مؤقتاً؟",
        trip: trip,
      };
    },
    promptResumeTrip(trip) {
      this.actionConfirmDialog = {
        visible: true,
        loading: false,
        type: "resume",
        title: "استئناف الرحلة",
        message: "هل تريد استئناف هذه الرحلة وإعادتها للجدول التشغيلي؟",
        trip: trip,
      };
    },
    executeActionConfirm() {
      if (!this.actionConfirmDialog.trip) return;
      const trip = this.actionConfirmDialog.trip;
      const actionType = this.actionConfirmDialog.type;
      const endpoint = `/operations/trips/${trip.id}/${actionType}`;

      this.actionConfirmDialog.loading = true;

      axios
        .post(endpoint)
        .then((res) => {
          if (res.data && res.data.success) {
            this.notifyUser("success", "تم بنجاح", res.data.message);
            this.actionConfirmDialog.visible = false;
            // Refresh table
            this.fetchTrips();
          } else {
            throw new Error(res.data.message || "تعذر تنفيذ الإجراء.");
          }
        })
        .catch((err) => {
          console.error(`Error during trip ${actionType}:`, err);
          const msg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "تعذر تنفيذ الإجراء.";
          this.notifyUser("error", "خطأ", msg);
        })
        .finally(() => {
          this.actionConfirmDialog.loading = false;
        });
    },
    getStatusLabel(status) {
      switch (status) {
        case "not_started":
          return "لم تبدأ";
        case "running":
          return "في الطريق";
        case "completed":
          return "مكتملة";
        case "cancelled":
          return "ملغاة";
        default:
          return status;
      }
    },
    notifyUser(type, title, text) {
      if (this.$notify) {
        this.$notify({
          title: title || (type === "success" ? "تم بنجاح" : "خطأ"),
          text: text,
          type: type === "warn" ? "warn" : type,
        });
      } else if (this.$swal) {
        this.$swal(title || (type === "success" ? "تم بنجاح" : "خطأ"), text, type === "warn" ? "warning" : type);
      } else {
        alert(text);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.operations-container {
  font-family: inherit;
  color: #1E293B;
}

/* ==================== HEADER ==================== */
.op-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.op-badge-section {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #EEF2F6;
  border-radius: 8px;
  color: #082C69;
  font-size: 12px;
  font-weight: 700;
}

.op-scope-pill {
  padding: 4px 12px;
  background: #F0F9FF;
  border: 1px solid #BAE6FD;
  border-radius: 8px;
  color: #0369A1;
  font-size: 12px;
}

.op-no-scope-pill {
  padding: 4px 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #64748B;
  font-size: 12px;
}

.op-summary-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;

  &.summary-not_started {
    background: #EFF6FF;
    border: 1px solid #BFDBFE;
    color: #1E40AF;
  }

  &.summary-running {
    background: #ECFDF5;
    border: 1px solid #A7F3D0;
    color: #065F46;
  }

  &.summary-completed {
    background: #F1F5F9;
    border: 1px solid #CBD5E1;
    color: #334155;
  }

  &.summary-cancelled {
    background: #FEF2F2;
    border: 1px solid #FECACA;
    color: #991B1B;
  }
}

.op-pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10B981;
  display: inline-block;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  animation: pulse-green 1.8s infinite;
}

@keyframes pulse-green {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

.op-main-title {
  font-size: 24px;
  font-weight: 800;
  color: #082C69;
  margin: 0;
}

.op-subtitle {
  font-size: 14px;
  color: #64748B;
  margin-top: 4px;
  margin-bottom: 0;
}

.op-btn-refresh {
  height: 40px !important;
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 10px !important;
  color: #082C69 !important;
  padding: 0 16px !important;

  &:hover {
    background: #F8FAFC !important;
    border-color: #CBD5E1 !important;
  }
}

/* ==================== 4 STATUS TABS ==================== */
.op-status-tabs-wrapper {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.op-status-tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.op-tab-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  background: #F8FAFC;
  border: 1px solid transparent;
  border-radius: 10px;
  color: #64748B;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #F1F5F9;
    color: #082C69;
  }

  &.tab-active {
    background: #FFFFFF;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    border-color: #E2E8F0;

    &.tab-not-started {
      color: #1E40AF;
      border-bottom: 3px solid #2563EB;
      .tab-icon { color: #2563EB !important; }
    }

    &.tab-running {
      color: #065F46;
      border-bottom: 3px solid #10B981;
      .tab-icon { color: #10B981 !important; }
    }

    &.tab-completed {
      color: #082C69;
      border-bottom: 3px solid #082C69;
      .tab-icon { color: #082C69 !important; }
    }

    &.tab-cancelled {
      color: #991B1B;
      border-bottom: 3px solid #DC2626;
      .tab-icon { color: #DC2626 !important; }
    }
  }
}

.op-tab-counter {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 800;

  &.counter-not-started {
    background: #EFF6FF;
    color: #1E40AF;
  }

  &.counter-running {
    background: #ECFDF5;
    color: #065F46;
  }

  &.counter-completed {
    background: #F1F5F9;
    color: #334155;
  }

  &.counter-cancelled {
    background: #FEF2F2;
    color: #991B1B;
  }
}

/* ==================== FILTER BAR ==================== */
.op-filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  flex-wrap: wrap;
}

.filter-inputs-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-grow: 1;
  flex-wrap: wrap;
}

.filter-search-box {
  flex-grow: 2;
  min-width: 220px;
}

.filter-date-shortcuts {
  flex-wrap: wrap;
}

.op-date-chip {
  padding: 6px 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #F1F5F9;
    border-color: #CBD5E1;
  }

  &.chip-active {
    background: #EEF2F6;
    color: #082C69;
    border-color: #082C69;
    font-weight: 700;
  }
}

.filter-custom-date {
  min-width: 240px;
}

.op-filter-date {
  max-width: 140px;
}

.filter-select-box {
  flex-grow: 1;
  min-width: 150px;
}

.filter-actions-group {
  display: flex;
  align-items: center;
}

.op-filter-btn {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 14px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #F1F5F9;
    border-color: #CBD5E1;
  }
}

.op-per-page-select {
  padding: 2px 6px;
  border: 1px solid #CBD5E1;
  border-radius: 6px;
  background: #FFFFFF;
  font-size: 12px;
  font-weight: 600;
  color: #082C69;
  cursor: pointer;
}

/* ==================== TABLES ==================== */
.op-main-table-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.op-data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.op-data-table th {
  padding: 12px 14px;
  background-color: #F8FAFC;
  color: #475569;
  font-weight: 700;
  text-align: right;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
}

.op-table-row {
  border-bottom: 1px solid #F1F5F9;
  transition: all 0.15s ease;

  &:hover {
    background-color: #F8FAFC;
  }
}

.op-table-row td {
  padding: 12px 14px;
  vertical-align: middle;
}

/* Table Pills & Badges */
.op-trip-code-pill {
  display: inline-block;
  padding: 3px 8px;
  background: #EEF2F6;
  border-radius: 6px;
  color: #082C69;
  font-size: 12px;
  font-weight: 700;
}

.text-danger {
  color: #DC2626 !important;
}

.op-parent-ref {
  font-size: 10px;
  color: #64748B;
}

.op-route-code-pill {
  display: inline-block;
  padding: 1px 6px;
  background: #F1F5F9;
  border-radius: 4px;
  color: #64748B;
  font-size: 11px;
}

.op-bookings-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #082C69;
  font-size: 11px;
}

.op-driver-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #EEF2F6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.op-badge-unassigned {
  display: inline-block;
  padding: 3px 8px;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 6px;
  color: #B45309;
  font-size: 11px;
  font-weight: 700;
}

.op-relative-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;

  &.rel-slate {
    background: #F1F5F9;
    color: #475569;
    border: 1px solid #CBD5E1;
  }
  &.rel-info {
    background: #EFF6FF;
    color: #1D4ED8;
    border: 1px solid #BFDBFE;
  }
  &.rel-warning {
    background: #FFFBEB;
    color: #B45309;
    border: 1px solid #FDE68A;
  }
  &.rel-error {
    background: #FEF2F2;
    color: #991B1B;
    border: 1px solid #FECACA;
  }
}

.op-live-running-badge {
  padding: 4px 10px;
  background: #ECFDF5;
  border: 1px solid #A7F3D0;
  border-radius: 8px;
  color: #065F46;
  font-size: 11px;
  font-weight: 700;
}

.op-timing-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;

  &.timing-on_time {
    background: #F0FDF4;
    color: #166534;
    border: 1px solid #BBF7D0;
  }
  &.timing-early {
    background: #ECFDF5;
    color: #065F46;
    border: 1px solid #A7F3D0;
  }
  &.timing-late {
    background: #FEF2F2;
    color: #991B1B;
    border: 1px solid #FECACA;
  }
}

.op-cancellation-badge {
  padding: 6px 10px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  color: #991B1B;
  font-size: 11px;
  line-height: 1.4;
  max-width: 380px;

  .reason-text {
    color: #991B1B;
  }
}

.op-btn-view {
  height: 28px !important;
  background: #FFFFFF !important;
  border: 1px solid #CBD5E1 !important;
  border-radius: 6px !important;
  color: #082C69 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  padding: 0 10px !important;

  &:hover {
    background: #082C69 !important;
    color: #FFFFFF !important;
    border-color: #082C69 !important;
  }
}

.op-btn-menu-icon {
  background: #F8FAFC !important;
  border-radius: 6px !important;

  &:hover {
    background: #E2E8F0 !important;
  }
}

.op-action-menu-list {
  border-radius: 8px;
}

/* ==================== FOOTER & PAGINATION ==================== */
.op-table-footer {
  background: #F8FAFC;
  border-top: 1px solid #E2E8F0;
}

.op-page-btn {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 10px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #F1F5F9;
    border-color: #CBD5E1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.op-page-number {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #F1F5F9;
  }

  &.page-active {
    background: #082C69;
    color: #FFFFFF;
    border-color: #082C69;
  }
}

.op-page-ellipsis {
  padding: 0 4px;
  color: #94A3B8;
}

/* ==================== MODAL ==================== */
.op-dialog-card {
  border-radius: 14px;
}

.op-dialog-header {
  background: #FAFCFE;
}

.op-icon-header-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #EEF2F6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.op-dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: #082C69;
}

.op-modal-cancel-banner {
  background: #FEF2F2;
  border: 1px solid #FECACA;
}

.op-trip-modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
}

.op-modal-stat-card {
  padding: 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
}

.op-modal-stat-card .stat-label {
  font-size: 11px;
  color: #64748B;
  margin-bottom: 4px;
}

.op-modal-stat-card .stat-value {
  font-size: 14px;
  font-weight: 700;
}

.op-modal-vehicle-card {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.op-modal-map-container {
  height: 280px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #E2E8F0;
}

.op-map-element {
  width: 100%;
  height: 100%;
}

.op-trips-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.op-trips-table th {
  padding: 10px 14px;
  background-color: #F1F5F9;
  color: #475569;
  font-weight: 700;
  text-align: right;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
}

.op-trips-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}

.op-trip-status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;

  &.status-not_started {
    background: #EFF6FF;
    color: #1E40AF;
    border: 1px solid #BFDBFE;
  }
  &.status-running {
    background: #ECFDF5;
    color: #065F46;
    border: 1px solid #A7F3D0;
  }
  &.status-completed {
    background: #F1F5F9;
    color: #475569;
    border: 1px solid #CBD5E1;
  }
  &.status-cancelled {
    background: #FEF2F2;
    color: #991B1B;
    border: 1px solid #FECACA;
  }
  &.status-unassigned {
    background: #FFFBEB;
    color: #B45309;
    border: 1px solid #FDE68A;
  }
}

.op-btn-secondary {
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  color: #475569 !important;
}

.op-confirm-card {
  border-radius: 14px;
}

/* Helpers */
.text-navy { color: #082C69 !important; }
.text-emerald { color: #059669 !important; }
.text-amber { color: #D97706 !important; }
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

/* Empty state */
.op-unassigned-card, .op-empty-table-card {
  background: #FFFFFF;
  border: 1px dashed #CBD5E1;
  border-radius: 18px;
}

.op-empty-icon-circle {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.op-empty-title {
  font-size: 19px;
  font-weight: 700;
  color: #1E293B;
}

.op-empty-desc {
  max-width: 520px;
  font-size: 13px;
  color: #64748B;
  line-height: 1.6;
}
</style>
